/**
 * The Workflow Studio's Optimization tab — RO-3-02b.
 *
 * Port of five files: `_optimize-live.tsx` (193 LOC, the tab shell),
 * `_objective.tsx` (104), `_variants.tsx` (138), `_promotions.tsx` (76) and
 * `_pipeline-actions.tsx` (89).
 *
 * The IA is the audited one (`#44207` — "the Optimization tab leads with
 * measurement, on derived numbers") and is preserved exactly: Objective →
 * Ladder → Variants → Promotions → Eval, every panel an `@excellent/ui` block,
 * no fixture decks and no local reducers.
 *
 * ── The gate this tab exists to route THROUGH, not around ──
 *
 * Three of these controls change production, and each one says so before it
 * acts. That is not decoration: `routeStep` (here, "Propose routing") writes a
 * CANDIDATE variant, and only `Promote → active` puts it in production. Keeping
 * those two separate is what stops a click from putting a model into production
 * without a candidate, an eval, and an approval — so the confirm() prompts and
 * the "this changes production" copy are carried over verbatim.
 */

import * as React from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  EmptyState,
  EvalPanel,
  FormField,
  Input,
  IntentBadge,
  JudgeCalibration,
  LadderView,
  ObjectiveEditor,
  ParetoChart,
  PromotionGateCard,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SectionHeading,
  VariantDiff,
  VariantTimeline,
  formatDateTime,
  type LoopVariantStatus,
  type PromotionGateItem,
  type VariantTimelineItem,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import {
  workflowDetail,
  type WorkflowDetailPayload,
  type WorkflowOptimizeDto,
} from "@excellent/ui/api/contracts/workflows/detail";

/** Every action on this tab reports through one envelope, as the actions did. */
type ActResult = { ok: boolean; data?: unknown; error?: string };
function failureText(res: ActResult): string {
  return res.error ?? (res.data as { reason?: string } | undefined)?.reason ?? "unknown";
}

export function OptimizeTab({
  slug,
  optimize,
  ladderSteps,
  onChanged,
}: {
  slug: string;
  optimize: WorkflowOptimizeDto;
  ladderSteps: WorkflowDetailPayload["ladderSteps"];
  /** Re-read the studio payload after a write — the `router.refresh()` seam. */
  onChanged: () => void;
}) {
  const {
    objective,
    gatePolicy,
    role,
    variants,
    promptDiff,
    driftVariantIds,
    promotions,
    evalCases,
    rubric,
    paretoPoints,
    pendingObjective,
  } = optimize;

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <SectionHeading as="h2" title="Objective" />
        <ObjectivePanel
          slug={slug}
          objective={objective}
          paretoPoints={paretoPoints}
          pending={pendingObjective}
          onChanged={onChanged}
        />
      </section>

      <section className="flex flex-col gap-3">
        <SectionHeading as="h2" title="Ladder" />
        <Card>
          <CardContent className="flex flex-col gap-4">
            {ladderSteps.length === 0 ? (
              <p className="type-body text-muted-foreground">
                No steps defined for this workflow yet.
              </p>
            ) : (
              <LadderView steps={ladderSteps} variant="full" gateLabel={gatePolicy} />
            )}
            <div className="border-t border-border/40 pt-4">
              <RouteDownPanel
                slug={slug}
                rungOneSteps={ladderSteps.filter((s) => s.rung === 1).map((s) => s.name)}
              />
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="flex flex-col gap-3">
        <SectionHeading as="h2" title="Variants" />
        {variants.length === 0 ? (
          <Card>
            <CardContent className="py-6">
              <p className="type-body text-muted-foreground">
                No variants yet. The optimizer records candidates here when it proposes prompt or
                topology changes.
              </p>
            </CardContent>
          </Card>
        ) : (
          <VariantsPanel
            slug={slug}
            role={role}
            variants={variants.map(
              (v): VariantTimelineItem => ({
                id: v.id,
                version: v.version,
                // The ledger's own status vocabulary; the timeline item narrows it.
                state: v.state as LoopVariantStatus,
                created_by: v.created_by,
                rationale: v.rationale,
                eval_summary: v.eval_summary,
                created_at: v.created_at,
              }),
            )}
            diff={promptDiff}
            driftVariantIds={driftVariantIds}
          />
        )}
      </section>

      <section className="flex flex-col gap-3">
        <SectionHeading as="h2" title="Promotions" />
        <Card>
          <CardContent className="pt-4">
            <PromotionsPanel
              slug={slug}
              promotions={promotions.map(
                (p): PromotionGateItem => ({
                  id: p.id,
                  step_name: p.step_name,
                  from_rung: p.from_rung,
                  to_rung: p.to_rung,
                  artifact_path: p.artifact_path,
                  artifact_hash: p.artifact_hash,
                  state: p.state as PromotionGateItem["state"],
                  drift: p.drift,
                }),
              )}
            />
          </CardContent>
        </Card>
      </section>

      <section className="flex flex-col gap-3">
        <SectionHeading as="h2" title="Eval" />
        <JudgeCalibration
          data={
            rubric
              ? {
                  judge_model: rubric.judge_model,
                  agreement: rubric.agreement,
                  updated_at: rubric.updated_at,
                  in_house: !/gpt|claude|gemini|opus|sonnet/i.test(rubric.judge_model),
                }
              : null
          }
        />
        <EvalPanel cases={evalCases} caseCount={evalCases.length} />
      </section>
    </div>
  );
}

// ── Objective (port of `_objective.tsx`) ─────────────────────────────────────

/**
 * An objective edit is high-impact, so a save is a PROPOSAL: it stages a pending
 * change and only a second, explicit sign-off makes it live. Two auditable steps,
 * which is why the pending banner is a card of its own rather than a toast.
 */
function ObjectivePanel({
  slug,
  objective,
  paretoPoints,
  pending,
  onChanged,
}: {
  slug: string;
  objective: string;
  paretoPoints: WorkflowOptimizeDto["paretoPoints"];
  pending: WorkflowOptimizeDto["pendingObjective"];
  onChanged: () => void;
}) {
  const [msg, setMsg] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  return (
    <div className="flex flex-col gap-4">
      {pending && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 py-3">
            <span className="type-body text-warning">Objective change pending sign-off</span>
            <span className="type-subtext text-muted-foreground">
              proposed {formatDateTime(pending.proposed_at)} by {pending.proposed_by} · not yet live
            </span>
            <div className="ml-auto flex gap-2">
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  void (async () => {
                    try {
                      const r = await apiCall(workflowDetail.approveObjective, {
                        params: { slug },
                      });
                      setMsg(r.ok ? "Approved — objective is now live." : `Failed: ${r.error}`);
                      onChanged();
                    } finally {
                      setBusy(false);
                    }
                  })();
                }}
              >
                Approve (sign off)
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  void (async () => {
                    try {
                      await apiCall(workflowDetail.discardObjective, { params: { slug } });
                      setMsg("Discarded pending change.");
                      onChanged();
                    } finally {
                      setBusy(false);
                    }
                  })();
                }}
              >
                Discard
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {msg && <p className="type-subtext text-muted-foreground">{msg}</p>}
      <ObjectiveEditor
        initial={pending ? pending.objective : objective}
        onSave={async (json) => {
          const r = await apiCall(workflowDetail.setObjective, {
            params: { slug },
            body: { objective: json },
          });
          setMsg(
            r.ok
              ? "Saved as a proposal — pending sign-off (not yet live)."
              : `Failed: ${r.error}`,
          );
          onChanged();
        }}
      />
      <ParetoChart points={paretoPoints} />
    </div>
  );
}

// ── Variants (port of `_variants.tsx`) ───────────────────────────────────────

/**
 * The variant timeline with its gated actions. Every action carries the eval
 * evidence and nothing high-impact is silent — the two that change production
 * (Promote, Roll back) confirm first and say so in the prompt.
 */
function VariantsPanel({
  slug,
  role,
  variants,
  diff,
  driftVariantIds = [],
}: {
  slug: string;
  role: string;
  variants: VariantTimelineItem[];
  diff: WorkflowOptimizeDto["promptDiff"];
  /** Variant ids whose artifact step snapshot has drifted from the live steps. */
  driftVariantIds?: number[];
}) {
  const [pending, setPending] = React.useState(false);
  const [msg, setMsg] = React.useState<{ ok: boolean; text: string } | null>(null);
  const driftIds = new Set(driftVariantIds);

  const act = (label: string, fn: () => Promise<ActResult>) => {
    setPending(true);
    setMsg(null);
    void (async () => {
      try {
        const res = await fn();
        setMsg({ ok: res.ok, text: res.ok ? `${label} ✓` : `${label} failed: ${failureText(res)}` });
      } finally {
        setPending(false);
      }
    })();
  };

  const hasLastGood = variants.some((v) => v.state === "last_good");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={pending}
          onClick={() =>
            act("Optimize now (proposed candidate)", () =>
              apiCall(workflowDetail.optimizeNow, { params: { slug }, body: { role } }),
            )
          }
        >
          Optimize now
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={pending || !hasLastGood}
          onClick={() => {
            if (confirm("Roll back the active variant to last_good? This changes production.")) {
              act("Rolled back to last_good", () =>
                apiCall(workflowDetail.rollback, { params: { slug } }),
              );
            }
          }}
        >
          Roll back to last_good
        </Button>
        {pending && <span className="type-subtext text-muted-foreground">working…</span>}
      </div>

      {msg && (
        <Alert variant={msg.ok ? "default" : "destructive"}>
          <AlertDescription>{msg.text}</AlertDescription>
        </Alert>
      )}

      <VariantTimeline
        variants={variants}
        rowActions={(v) => {
          // The variant's captured step-list no longer matches the live steps — a
          // topology drift. VariantTimeline owns the state column, so the badge
          // rides the per-row action slot beside it.
          const drift = driftIds.has(v.id) ? (
            <span title="variant snapshot differs from live steps">
              <IntentBadge intent="warning">topology drift</IntentBadge>
            </span>
          ) : null;
          const promote =
            v.state === "candidate" || v.state === "shadow" || v.state === "canary" ? (
              <Button
                size="sm"
                variant="outline"
                disabled={pending}
                onClick={() => {
                  if (confirm(`Promote v${v.version} to active? This changes production.`)) {
                    act(`Promoted v${v.version} → active`, () =>
                      apiCall(workflowDetail.promoteVariant, {
                        params: { slug },
                        body: { variantId: v.id, toState: "active" },
                      }),
                    );
                  }
                }}
              >
                Promote → active
              </Button>
            ) : null;
          if (!drift && !promote) return null;
          return (
            <span className="flex items-center justify-end gap-2">
              {drift}
              {promote}
            </span>
          );
        }}
      />

      {diff && (
        <VariantDiff
          incumbentPrompt={diff.incumbentPrompt}
          candidatePrompt={diff.candidatePrompt}
          scoreboard={diff.scoreboard}
          label="Active vs latest candidate"
        />
      )}
    </div>
  );
}

// ── Promotions (port of `_promotions.tsx`) ───────────────────────────────────

/** Rung-④ agent→code promotions. Proposed/shadow carry the human gate. */
function PromotionsPanel({
  slug,
  promotions,
}: {
  slug: string;
  promotions: PromotionGateItem[];
}) {
  const [pending, setPending] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);

  if (promotions.length === 0) {
    return <EmptyState variant="inline" title="No agent→code promotions yet" />;
  }

  const decide = (fn: () => Promise<ActResult>, settle: (r: ActResult) => string) => {
    setPending(true);
    void (async () => {
      try {
        setMsg(settle(await fn()));
      } finally {
        setPending(false);
      }
    })();
  };

  return (
    <div className="flex flex-col gap-2">
      {msg && <p className="type-subtext text-muted-foreground">{msg}</p>}
      {promotions.map((p) => (
        <PromotionGateCard
          key={p.id}
          promotion={p}
          actions={
            p.state === "proposed" || p.state === "shadow" ? (
              <>
                <Button
                  size="sm"
                  disabled={pending}
                  onClick={() => {
                    if (
                      confirm(
                        `Approve promoting “${p.step_name}” to a rung-④ script? This changes production (drift-monitored, reversible).`,
                      )
                    ) {
                      decide(
                        () =>
                          apiCall(workflowDetail.approvePromotion, {
                            params: { slug },
                            body: { promotionId: p.id },
                          }),
                        (r) =>
                          r.ok
                            ? `Approved promotion #${p.id} — step is now a monitored script.`
                            : `Approve failed: ${failureText(r)}`,
                      );
                    }
                  }}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pending}
                  onClick={() =>
                    decide(
                      () =>
                        apiCall(workflowDetail.rejectPromotion, {
                          params: { slug },
                          body: { promotionId: p.id },
                        }),
                      (r) =>
                        r.ok
                          ? `Rejected promotion #${p.id} — step stays a model.`
                          : `Reject failed: ${failureText(r)}`,
                    )
                  }
                >
                  Reject
                </Button>
              </>
            ) : null
          }
        />
      ))}
    </div>
  );
}

// ── Route-a-step-down (port of `_pipeline-actions.tsx`) ──────────────────────

/**
 * Propose routing a rung-① step down to a small model (rung ②) with
 * confidence-based escalation. Produces a CANDIDATE variant shown in the Variants
 * panel above — it does NOT change production directly; promotion is gated there.
 */
function RouteDownPanel({ slug, rungOneSteps }: { slug: string; rungOneSteps: string[] }) {
  const [pending, setPending] = React.useState(false);
  const [step, setStep] = React.useState(rungOneSteps[0] ?? "");
  const [model, setModel] = React.useState("claude-haiku-4-5");
  const [msg, setMsg] = React.useState<{ ok: boolean; text: string } | null>(null);

  if (rungOneSteps.length === 0) {
    return (
      <p className="type-subtext text-muted-foreground">
        All steps are already below rung ①. Nothing to route down.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="type-body text-foreground">Route a step to a small model (rung ②)</p>
      <div className="flex flex-wrap items-end gap-2">
        <FormField label="Step" className="w-48">
          <Select value={step} onValueChange={setStep}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {rungOneSteps.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Small model" htmlFor="route-model" className="w-48">
          <Input
            id="route-model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="small model id"
          />
        </FormField>
        <Button
          size="sm"
          disabled={pending || !step || !model}
          onClick={() => {
            setPending(true);
            setMsg(null);
            void (async () => {
              try {
                const res = await apiCall(workflowDetail.routeStep, {
                  params: { slug },
                  body: { step, model },
                });
                setMsg({
                  ok: res.ok,
                  text: res.ok
                    ? "Proposed routing candidate — promote it in the Variants panel."
                    : `Failed: ${failureText(res)}`,
                });
              } finally {
                setPending(false);
              }
            })();
          }}
        >
          {pending ? "Proposing…" : "Propose routing"}
        </Button>
      </div>
      {msg && (
        <Alert variant={msg.ok ? "default" : "destructive"}>
          <AlertDescription>{msg.text}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
